import axios from "axios";
import { useState } from "react";
import * as Sentry from "@sentry/browser";
import { useQuizContext } from "@/pages/quiz/context";
import { verifySsn } from "@/api/vouched";
import { VerifySsnStatus, VerifySsnSubmitted } from "@/api/vouched/types";

export type VerifyOutcome =
  | { kind: "success" }
  // status "error" -> correction screen (or handoff when attemptsLeft is 0)
  | {
      kind: "mismatch";
      submitted?: VerifySsnSubmitted;
      attemptsLeft: number;
    }
  // status "invalid_request_phone_number" -> correction screen, phone banner
  | { kind: "invalidPhone"; submitted?: VerifySsnSubmitted }
  // rate limited (HTTP 429) -> hand off to the photo-ID flow
  | { kind: "rateLimited" }
  // technical error / thrown (non-429) -> stay and show an inline error,
  // unless attemptsLeft is 0 (then the caller hands off)
  | { kind: "error"; attemptsLeft?: number };

export interface VerifyParams {
  ssnLastFour: string;
  firstName?: string;
  lastName?: string;
  /** ISO date, e.g. "1990-01-31" */
  dateOfBirth?: string;
  /** 10 digits, e.g. "2065551111" */
  phoneNumber?: string;
}

export const useVerifySsn = () => {
  const { voucherId } = useQuizContext();
  const [isLoading, setIsLoading] = useState(false);

  const verify = async (params: VerifyParams): Promise<VerifyOutcome> => {
    setIsLoading(true);

    try {
      const result = await verifySsn({
        id: voucherId ?? "",
        ssn_last_four: params.ssnLastFour,
        first_name: params.firstName ?? null,
        last_name: params.lastName ?? null,
        date_of_birth: params.dateOfBirth ?? null,
        phone_number: params.phoneNumber ?? null,
      });

      if (result.status === VerifySsnStatus.SUCCESS) {
        return { kind: "success" };
      }

      // Any "error" status goes to the correction screen; the caller sends the
      // user to handoff when attemptsLeft is 0.
      if (result.status === VerifySsnStatus.ERROR) {
        return {
          kind: "mismatch",
          submitted: result.submitted,
          attemptsLeft: result.attempts_left ?? 0,
        };
      }

      // No check ran, so there is no attempts_left to act on here.
      if (result.status === VerifySsnStatus.INVALID_REQUEST_PHONE_NUMBER) {
        return { kind: "invalidPhone", submitted: result.submitted };
      }

      // technical_error (and anything unexpected) -> inline error, unless the
      // order is out of attempts (handled by the caller).
      // A wire `null` would satisfy `<= 0` downstream and latch Sumsub shut.
      return { kind: "error", attemptsLeft: result.attempts_left ?? undefined };
    } catch (error) {
      Sentry.captureException(error);
      // Rate limited -> too many attempts, hand off to the photo-ID flow.
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        return { kind: "rateLimited" };
      }
      return { kind: "error" };
    } finally {
      setIsLoading(false);
    }
  };

  return { verify, isLoading };
};
